'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CancelTokenSource } from 'axios';

import { getQueryClient } from 'app/get-query-client';
import { useAuthStore } from 'lib/stores/authStore';
import type { ServerError } from 'models';
import {
    ORDER_AUTO_CANCEL_SELF_TRADE_DISABLED_KEY,
    PERPS_ALERT_DISABLED_KEYS,
    type NotificationPreferences,
    type NotificationType,
    type PerpsAlertDisabledKey,
} from 'models/subscriptions';
import { handleSnackbarError } from 'stores/zustand/snackbarStore';
import {
    changeBothPreferences as changeBothPreferencesTransport,
    changeEmailPreference as changeEmailPreferenceTransport,
    deleteNotificationSettings,
    getNotificationPreferences,
    getNotificationSettings,
    updateNotificationSettings,
} from 'transport/domains/notifications';

// 1000ms gives the average burst of pill clicks (channel + multiple
// categories) plenty of room to coalesce into one round-trip. Anything
// shorter clipped users mid-burst on slower interactions. The
// click-immediately-leave failure mode is covered by a pagehide flush in
// Settings.tsx, so we're free to be aggressive here.
const NOTIFICATION_FLUSH_DEBOUNCE_MS = 1000;

export interface NotificationPreferencesVo {
    email: NotificationPreferences;
    push: NotificationPreferences;
}

export const notificationPreferencesKeys = {
    all: ['notificationPreferences'] as const,
    user: (userId: string) => [...notificationPreferencesKeys.all, userId] as const,
};

export const makerTradeBatchingIntervalKeys = {
    all: ['makerTradeBatchingInterval'] as const,
    user: (userId: string) => [...makerTradeBatchingIntervalKeys.all, userId] as const,
};

export const perpsAlertsSettingsKeys = {
    all: ['perpsAlertsSettings'] as const,
    user: (userId: string) => [...perpsAlertsSettingsKeys.all, userId] as const,
};

export const orderAutoCancelSettingsKeys = {
    all: ['orderAutoCancelSettings'] as const,
    user: (userId: string) => [...orderAutoCancelSettingsKeys.all, userId] as const,
};

export const sportsNotificationSettingsKeys = {
    all: ['sportsNotificationSettings'] as const,
    user: (userId: string) => [...sportsNotificationSettingsKeys.all, userId] as const,
};

/**
 * Last-resort sport ids when the notification catalog is unavailable.
 * Prefer `catalogSportFilterIds(catalog)` — the catalog is the source of truth.
 */
export const VALID_SPORT_SLUGS = ['baseball', 'basketball', 'golf', 'hockey', 'soccer', 'tennis'] as const;
export type SportSlug = (typeof VALID_SPORT_SLUGS)[number];

/** Catalog sport ids when present; otherwise the hardcoded fallback allow-list. */
export function resolveKnownSportIds(sportIds?: readonly string[]): readonly string[] {
    return sportIds && sportIds.length > 0 ? sportIds : VALID_SPORT_SLUGS;
}

/** Apply a sport toggle, ordering by known ids and preserving catalog sports. */
export function nextEnabledSportsList(
    previous: readonly string[],
    sport: string,
    enabled: boolean,
    knownSportIds?: readonly string[],
): string[] {
    const known = resolveKnownSportIds(knownSportIds);
    const set = new Set(previous);
    if (enabled) set.add(sport);
    else set.delete(sport);
    const next = known.filter((s) => set.has(s));
    // Keep any still-enabled ids outside the known list so a save does not
    // silently wipe server-side sports the catalog has not listed yet.
    for (const s of set) {
        if (!next.includes(s)) next.push(s);
    }
    return next;
}

export function fetchNotificationPreferences(userId: string): Promise<NotificationPreferencesVo> {
    return new Promise((resolve, reject) => {
        let settled = false;
        getNotificationPreferences(
            userId,
            (prefs) => {
                settled = true;
                resolve(prefs);
            },
            (err) => {
                settled = true;
                reject(err);
            },
            () => {
                if (!settled) {
                    settled = true;
                    reject(new Error('notification preferences request did not settle'));
                }
            },
        );
    });
}

export function fetchMakerTradeBatchingInterval(userId: string): Promise<number> {
    return new Promise((resolve, reject) => {
        let settled = false;
        getNotificationSettings(
            userId,
            'maker_trade',
            (interval) => {
                settled = true;
                resolve(interval);
            },
            (err) => {
                settled = true;
                reject(err);
            },
            () => {
                if (!settled) {
                    settled = true;
                    reject(new Error('maker trade batching interval request did not settle'));
                }
            },
        );
    });
}

export function fetchPerpsAlertsSettings(userId: string): Promise<Record<PerpsAlertDisabledKey, boolean>> {
    return new Promise((resolve, reject) => {
        let settled = false;
        getNotificationSettings(
            userId,
            'perps_alerts',
            (_interval, settings) => {
                settled = true;
                resolve(
                    Object.fromEntries(
                        PERPS_ALERT_DISABLED_KEYS.map((key) => [key, settings?.[key] === true]),
                    ) as Record<PerpsAlertDisabledKey, boolean>,
                );
            },
            (err) => {
                settled = true;
                reject(err);
            },
            () => {
                if (!settled) {
                    settled = true;
                    reject(new Error('perps alerts settings request did not settle'));
                }
            },
        );
    });
}

export function fetchOrderAutoCancelSettings(userId: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
        let settled = false;
        getNotificationSettings(
            userId,
            'order_auto_canceled',
            (_interval, settings) => {
                settled = true;
                resolve(settings?.[ORDER_AUTO_CANCEL_SELF_TRADE_DISABLED_KEY] === true);
            },
            (err) => {
                settled = true;
                reject(err);
            },
            () => {
                if (!settled) {
                    settled = true;
                    reject(new Error('order auto-cancel settings request did not settle'));
                }
            },
        );
    });
}

/** Fail-open: missing settings ⇒ every known sport enabled (matches send path / mobile BFF). */
export function fetchSportsNotificationSettings(userId: string, sportIds?: readonly string[]): Promise<string[]> {
    const failOpen = [...resolveKnownSportIds(sportIds)];
    return new Promise((resolve, reject) => {
        let settled = false;
        getNotificationSettings(
            userId,
            'personalized_sports_notification',
            (_interval, settings) => {
                settled = true;
                const enabled = settings?.enabled_sports;
                if (Array.isArray(enabled)) {
                    resolve(enabled.filter((s): s is string => typeof s === 'string'));
                    return;
                }
                resolve(failOpen);
            },
            (err) => {
                settled = true;
                if (err?.notFound) {
                    resolve(failOpen);
                    return;
                }
                reject(err);
            },
            () => {
                if (!settled) {
                    settled = true;
                    reject(new Error('sports notification settings request did not settle'));
                }
            },
        );
    });
}

export function notificationPreferencesQueryOptions(userId: string) {
    return {
        queryKey: notificationPreferencesKeys.user(userId),
        queryFn: () => fetchNotificationPreferences(userId),
    };
}

export function makerTradeBatchingIntervalQueryOptions(userId: string) {
    return {
        queryKey: makerTradeBatchingIntervalKeys.user(userId),
        queryFn: () => fetchMakerTradeBatchingInterval(userId),
    };
}

export function perpsAlertsSettingsQueryOptions(userId: string) {
    return {
        queryKey: perpsAlertsSettingsKeys.user(userId),
        queryFn: () => fetchPerpsAlertsSettings(userId),
    };
}

export function orderAutoCancelSettingsQueryOptions(userId: string) {
    return {
        queryKey: orderAutoCancelSettingsKeys.user(userId),
        queryFn: () => fetchOrderAutoCancelSettings(userId),
    };
}

export function sportsNotificationSettingsQueryOptions(userId: string, sportIds?: readonly string[]) {
    return {
        queryKey: sportsNotificationSettingsKeys.user(userId),
        queryFn: () => fetchSportsNotificationSettings(userId, sportIds),
    };
}

export function useNotificationPreferencesQuery() {
    const userId = useAuthStore((s) => s.userId);

    return useQuery({
        ...notificationPreferencesQueryOptions(userId ?? ''),
        enabled: !!userId,
    });
}

export function useMakerTradeBatchingIntervalQuery() {
    const userId = useAuthStore((s) => s.userId);
    const isLoggedInMember = useAuthStore((s) => s.isLoggedInMember);

    return useQuery({
        ...makerTradeBatchingIntervalQueryOptions(userId ?? ''),
        enabled: !!userId && isLoggedInMember,
    });
}

export function usePerpsAlertsSettingsQuery(enabled = true) {
    const userId = useAuthStore((s) => s.userId);
    const isLoggedInMember = useAuthStore((s) => s.isLoggedInMember);

    return useQuery({
        ...perpsAlertsSettingsQueryOptions(userId ?? ''),
        enabled: enabled && !!userId && isLoggedInMember,
    });
}

export function useOrderAutoCancelSettingsQuery() {
    const userId = useAuthStore((s) => s.userId);
    const isLoggedInMember = useAuthStore((s) => s.isLoggedInMember);

    return useQuery({
        ...orderAutoCancelSettingsQueryOptions(userId ?? ''),
        enabled: !!userId && isLoggedInMember,
    });
}

export function useSportsNotificationSettingsQuery(enabled = true, sportIds?: readonly string[]) {
    const userId = useAuthStore((s) => s.userId);
    const isLoggedInMember = useAuthStore((s) => s.isLoggedInMember);

    return useQuery({
        ...sportsNotificationSettingsQueryOptions(userId ?? '', sportIds),
        enabled: enabled && !!userId && isLoggedInMember,
    });
}

export function useSportsNotificationSettingsData(enabled = true, sportIds?: readonly string[]) {
    const query = useSportsNotificationSettingsQuery(enabled, sportIds);
    return {
        enabledSports: query.data ?? [...resolveKnownSportIds(sportIds)],
        loadingSportsSettings: query.isPending,
    };
}

export interface NotificationPreferencesData {
    emailPreference: NotificationPreferences;
    pushPreference: NotificationPreferences;
    loadingNotificationPreferences: boolean;
    // Sticky: only clears on a successful load, matching the original
    // UserStore field — a failed initial load leaves push edits locked out
    // forever rather than risk overwriting saved push subs with a stale [].
    loadingPushPreferences: boolean;
}

export function useNotificationPreferencesData(): NotificationPreferencesData {
    const query = useNotificationPreferencesQuery();

    return {
        emailPreference: query.data?.email ?? [],
        pushPreference: query.data?.push ?? [],
        loadingNotificationPreferences: query.isPending,
        loadingPushPreferences: query.isPending || query.isError,
    };
}

// --- Optimistic, non-debounced single write (email only) -------------------

export function changeEmailPreference(
    emailPreferences: NotificationPreferences,
    onSucMessage = 'Your email notification preferences were updated.',
): void {
    const userId = useAuthStore.getState().userId ?? '';
    const key = notificationPreferencesKeys.user(userId);
    const previous = getQueryClient().getQueryData<NotificationPreferencesVo>(key);
    getQueryClient().setQueryData<NotificationPreferencesVo>(key, (existing) => ({
        email: emailPreferences,
        push: existing?.push ?? [],
    }));

    changeEmailPreferenceTransport(
        userId,
        emailPreferences,
        onSucMessage,
        () => undefined,
        (err) => {
            getQueryClient().setQueryData(key, previous);
            handleSnackbarError(err);
        },
    );
}

// --- Optimistic, debounced write (email + push together) -------------------
//
// Debounce + in-flight-save-cancellation state lives at module scope (not in
// a React hook) so it survives being called both from event handlers and
// from a `pagehide` listener in Settings.tsx, exactly like the fields it
// replaces.

let notificationFlushTimeout: ReturnType<typeof setTimeout> | null = null;
let notificationFlushCancel: CancelTokenSource | null = null;
let notificationFlushSnapshot: NotificationPreferencesVo | null = null;
let notificationFlushPendingMessage = '';

function flushNotificationPreferences(onSucMessage: string): void {
    notificationFlushTimeout = null;
    const snapshot = notificationFlushSnapshot;
    notificationFlushSnapshot = null;
    const userId = useAuthStore.getState().userId ?? '';
    const key = notificationPreferencesKeys.user(userId);
    const current = getQueryClient().getQueryData<NotificationPreferencesVo>(key) ?? { email: [], push: [] };

    notificationFlushCancel = changeBothPreferencesTransport(
        userId,
        current.email,
        current.push,
        onSucMessage,
        () => {
            notificationFlushCancel = null;
        },
        (err) => {
            notificationFlushCancel = null;
            // Cancellation means a newer click superseded this save — local
            // state is already what the user wants and a fresh flush is
            // queued. Reverting would clobber the new state.
            if (err.cancelled) return;
            if (snapshot) {
                getQueryClient().setQueryData(key, snapshot);
            }
            handleSnackbarError(err);
        },
    );
}

// Optimistic + debounced write. Local state updates immediately so the
// pills never lag, but the Subscribe round-trip is deferred until the user
// stops clicking. A burst of clicks -> one network request, one toast. On
// error we revert all the way to the pre-batch state, not just the most
// recent click. An in-flight save is canceled when a new click arrives so
// the toast that lands always matches the latest state.
export function changeBothPreferences(
    emailPreferences: NotificationPreferences,
    pushPreferences: NotificationPreferences,
    onSucMessage = 'Your notification preferences were updated.',
): void {
    const userId = useAuthStore.getState().userId ?? '';
    const key = notificationPreferencesKeys.user(userId);

    if (notificationFlushSnapshot === null) {
        notificationFlushSnapshot = getQueryClient().getQueryData<NotificationPreferencesVo>(key) ?? {
            email: [],
            push: [],
        };
    }
    getQueryClient().setQueryData<NotificationPreferencesVo>(key, {
        email: emailPreferences,
        push: pushPreferences,
    });
    // Stash so a pagehide flush can use the caller's chosen message.
    // Latest-call wins; in practice all callers pass the default.
    notificationFlushPendingMessage = onSucMessage;

    if (notificationFlushCancel) {
        notificationFlushCancel.cancel('superseded by newer pref change');
        notificationFlushCancel = null;
    }
    if (notificationFlushTimeout) {
        clearTimeout(notificationFlushTimeout);
    }
    notificationFlushTimeout = setTimeout(() => {
        flushNotificationPreferences(notificationFlushPendingMessage);
    }, NOTIFICATION_FLUSH_DEBOUNCE_MS);
}

// Flush synchronously if a debounced save is pending. Called from a
// pagehide listener so closing/reloading the tab doesn't drop the user's
// most recent toggle. Safe to call when nothing is pending — it's a no-op.
export function flushNotificationPreferencesIfPending(): void {
    if (!notificationFlushTimeout) return;
    clearTimeout(notificationFlushTimeout);
    notificationFlushTimeout = null;
    flushNotificationPreferences(notificationFlushPendingMessage);
}

// Drops any pending debounced flush and cancels any in-flight save. Called
// from UserStore.endSession()/applyForcedDisconnectTeardown() so a timer
// scheduled while the user was signed in doesn't fire after logout (which
// would PATCH with a blank userId). Local state isn't reverted — the user
// is gone, the next login will load fresh prefs.
export function clearNotificationFlushPending(): void {
    if (notificationFlushTimeout) {
        clearTimeout(notificationFlushTimeout);
        notificationFlushTimeout = null;
    }
    if (notificationFlushCancel) {
        notificationFlushCancel.cancel('cleared on logout');
        notificationFlushCancel = null;
    }
    notificationFlushSnapshot = null;
    notificationFlushPendingMessage = '';
}

// --- Maker-trade email batching interval ------------------------------------

function updateBatchingInterval(
    batchInterval: number,
    onSucMessage: string,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
): void {
    const userId = useAuthStore.getState().userId ?? '';
    updateNotificationSettings(
        userId,
        'maker_trade',
        batchInterval,
        {},
        onSucMessage || 'Batched notifications enabled for maker trades',
        () => {
            getQueryClient().setQueryData(makerTradeBatchingIntervalKeys.user(userId), batchInterval);
            onSuc();
        },
        (err) => {
            handleSnackbarError(err);
            onErr(err);
        },
    );
}

export function updateMakerTradeBatchingInterval(
    batchInterval: number,
    onSucMessage = 'Notification preferences updated',
    onSuc: () => void = () => undefined,
    onErr: (err: ServerError) => void = () => undefined,
): void {
    const userId = useAuthStore.getState().userId ?? '';
    const key = notificationPreferencesKeys.user(userId);
    const current = getQueryClient().getQueryData<NotificationPreferencesVo>(key) ?? { email: [], push: [] };

    // Case 1: No Notifications (-1) - Remove maker_trade from email preferences
    if (batchInterval === -1) {
        const updatedPreferences = current.email.filter((pref) => pref !== 'maker_trade');
        changeEmailPreference(updatedPreferences, 'Email notifications disabled for maker trades');
        getQueryClient().setQueryData(makerTradeBatchingIntervalKeys.user(userId), null);
        onSuc();
        return;
    }

    // Case 2: Instant Notifications (0) - update both email preference and batching preference
    if (batchInterval === 0) {
        if (!current.email.includes('maker_trade')) {
            const updatedPreferences = [...current.email, 'maker_trade' as NotificationType];
            // Update email preferences silently first
            changeEmailPreferenceTransport(
                userId,
                updatedPreferences,
                '', // Silent update
                () => {
                    getQueryClient().setQueryData<NotificationPreferencesVo>(key, {
                        email: updatedPreferences,
                        push: current.push,
                    });
                },
                onErr,
            );
        }

        deleteNotificationSettings(
            userId,
            'maker_trade',
            'Instant email notifications enabled for maker trades',
            () => {
                getQueryClient().setQueryData(makerTradeBatchingIntervalKeys.user(userId), 0);
                onSuc();
            },
            (err) => {
                handleSnackbarError(err);
                onErr(err);
            },
        );
        return;
    }

    // Case 3: Batched Notifications - Ensure maker_trade is in preferences, then set batching
    if (!current.email.includes('maker_trade')) {
        const updatedPreferences = [...current.email, 'maker_trade' as NotificationType];
        // Update email preferences silently first
        changeEmailPreferenceTransport(
            userId,
            updatedPreferences,
            '', // Silent update
            () => {
                getQueryClient().setQueryData<NotificationPreferencesVo>(key, {
                    email: updatedPreferences,
                    push: current.push,
                });
                // Continue to update batching preference
                updateBatchingInterval(batchInterval, onSucMessage, onSuc, onErr);
            },
            onErr,
        );
    } else {
        // Email preference already set, just update batching
        updateBatchingInterval(batchInterval, onSucMessage, onSuc, onErr);
    }
}

// --- Perps alert opt-outs ---------------------------------------------------

export function usePerpsAlertsData(enabled = true) {
    const query = usePerpsAlertsSettingsQuery(enabled);
    const perpsAlertsDisabled: Record<PerpsAlertDisabledKey, boolean> =
        query.data ??
        (Object.fromEntries(PERPS_ALERT_DISABLED_KEYS.map((key) => [key, false])) as Record<
            PerpsAlertDisabledKey,
            boolean
        >);

    return {
        perpsAlertsDisabled,
        loadingPerpsAlertsSettings: query.isPending,
    };
}

// The backend replaces the whole perps_alerts settings JSONB on write, so we
// PUT all category keys together — callers should block concurrent saves
// (via this mutation's own isPending) so out-of-order writes can't clobber
// each other.
export function useSetPerpsAlertDisabledMutation() {
    const queryClient = useQueryClient();

    return useMutation<
        void,
        ServerError,
        { key: PerpsAlertDisabledKey; disabled: boolean },
        { previous?: Record<PerpsAlertDisabledKey, boolean> }
    >({
        mutationFn: ({ key, disabled }) => {
            const userId = useAuthStore.getState().userId ?? '';
            const previous =
                queryClient.getQueryData<Record<PerpsAlertDisabledKey, boolean>>(
                    perpsAlertsSettingsKeys.user(userId),
                ) ??
                (Object.fromEntries(PERPS_ALERT_DISABLED_KEYS.map((k) => [k, false])) as Record<
                    PerpsAlertDisabledKey,
                    boolean
                >);
            const next = { ...previous, [key]: disabled };

            return new Promise<void>((resolve, reject) => {
                updateNotificationSettings(
                    userId,
                    'perps_alerts',
                    0,
                    next,
                    disabled ? 'Perpetuals alert turned off' : 'Perpetuals alert turned on',
                    resolve,
                    reject,
                );
            });
        },
        onMutate: ({ key, disabled }) => {
            const userId = useAuthStore.getState().userId ?? '';
            const settingsKey = perpsAlertsSettingsKeys.user(userId);
            const previous = queryClient.getQueryData<Record<PerpsAlertDisabledKey, boolean>>(settingsKey);
            queryClient.setQueryData<Record<PerpsAlertDisabledKey, boolean>>(settingsKey, (existing) => ({
                ...(existing ??
                    (Object.fromEntries(PERPS_ALERT_DISABLED_KEYS.map((k) => [k, false])) as Record<
                        PerpsAlertDisabledKey,
                        boolean
                    >)),
                [key]: disabled,
            }));
            return { previous };
        },
        onError: (err, _vars, context) => {
            const userId = useAuthStore.getState().userId ?? '';
            queryClient.setQueryData(perpsAlertsSettingsKeys.user(userId), context?.previous);
            handleSnackbarError(err);
        },
    });
}

// --- Sports per-sport allow-list --------------------------------------------

export function useSetSportEnabledMutation(sportIds?: readonly string[]) {
    const queryClient = useQueryClient();
    const knownSportIds = resolveKnownSportIds(sportIds);

    return useMutation<void, ServerError, { sport: string; enabled: boolean }, { previous?: string[] }>({
        mutationFn: ({ sport, enabled }) => {
            const userId = useAuthStore.getState().userId ?? '';
            const previous = queryClient.getQueryData<string[]>(sportsNotificationSettingsKeys.user(userId)) ?? [
                ...knownSportIds,
            ];
            const next = nextEnabledSportsList(previous, sport, enabled, knownSportIds);

            return new Promise<void>((resolve, reject) => {
                updateNotificationSettings(
                    userId,
                    'personalized_sports_notification',
                    0,
                    { enabled_sports: next },
                    enabled ? 'Sport alerts turned on' : 'Sport alerts turned off',
                    resolve,
                    reject,
                );
            });
        },
        onMutate: ({ sport, enabled }) => {
            const userId = useAuthStore.getState().userId ?? '';
            const key = sportsNotificationSettingsKeys.user(userId);
            const previous = queryClient.getQueryData<string[]>(key);
            const base = previous ?? [...knownSportIds];
            queryClient.setQueryData<string[]>(key, nextEnabledSportsList(base, sport, enabled, knownSportIds));
            return { previous };
        },
        onError: (err, _vars, context) => {
            const userId = useAuthStore.getState().userId ?? '';
            queryClient.setQueryData(sportsNotificationSettingsKeys.user(userId), context?.previous);
            handleSnackbarError(err);
        },
    });
}

// --- Order auto-cancel / self-match-prevention (SMP) opt-out ---------------

// SMP auto-cancels are the only key in the order_auto_canceled settings JSONB
// today, so we PUT just that key.
export function useSetOrderAutoCancelSelfTradeDisabledMutation() {
    const queryClient = useQueryClient();

    return useMutation<void, ServerError, { disabled: boolean }, { previous?: boolean }>({
        mutationFn: ({ disabled }) => {
            const userId = useAuthStore.getState().userId ?? '';
            return new Promise<void>((resolve, reject) => {
                updateNotificationSettings(
                    userId,
                    'order_auto_canceled',
                    0,
                    { [ORDER_AUTO_CANCEL_SELF_TRADE_DISABLED_KEY]: disabled },
                    disabled ? 'Self-trade auto-cancel alerts turned off' : 'Self-trade auto-cancel alerts turned on',
                    resolve,
                    reject,
                );
            });
        },
        onMutate: ({ disabled }) => {
            const userId = useAuthStore.getState().userId ?? '';
            const key = orderAutoCancelSettingsKeys.user(userId);
            const previous = queryClient.getQueryData<boolean>(key);
            queryClient.setQueryData<boolean>(key, disabled);
            return { previous };
        },
        onError: (err, _vars, context) => {
            const userId = useAuthStore.getState().userId ?? '';
            queryClient.setQueryData(orderAutoCancelSettingsKeys.user(userId), context?.previous);
            handleSnackbarError(err);
        },
    });
}
